import * as React from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { useEffect } from "react";
import OutsideClickHandler from "@graffer-inc/react-outside-click-handler";

import { Loader } from "../../../organisms/common/loading";
import { useModal } from "../../../../utils/custom-hooks";
import { RBACButton } from "../../../molecules/rbac-button";
import { useAuthContext } from "../../../../auth-state";
import {
  UsersService,
  UsersServiceProps,
} from "../../../../application/service/smart-apply/users/users-service";
import { SettingsTemplate } from "../../../template/settings/default-template";
import { connectPageService } from "../../../../application/domain/base/base-application-service";
import { parseQueryOfLocation } from "../../../../utils/url-utils";
import { Dropdown } from "../../../molecules/dropdown";
import { SettingR } from "../route";
import { InformationBanner } from "@graffer-inc/console-design-kit";
import { BatchImportCreateUserModal } from "../../../organisms/settings/user-list/batch-import-create-user-modal";
import { BatchImportUpdateUserModal } from "../../../organisms/settings/user-list/batch-import-update-user-modal";
import { Pagination } from "@graffer-inc/console-design-kit";
import { UserCsvDownloadModal } from "../../../organisms/settings/user-list/user-csv-download-modal";
import { TenantUserList } from "../../../organisms/settings/user-list/users-list";
import { matchType } from "../../../../application/domain/base/base-type";
import { UsersSearchContainer } from "../../../organisms/settings/user-list/users-search";
import { UserEntity } from "../../../../application/domain/user/user-entity";
import { BatchChangeAuthMethodToSAMLModal } from "../../../organisms/settings/user-list/batch-change-auth-method-saml-modal";
import { BatchChangeAuthMethodToPasswordModal } from "../../../organisms/settings/user-list/batch-auth-change-method-modal-password";
import { ChangeAuthMethodService } from "../../../../application/service/settings/users/change-auth-method-service";

const UsersPage: React.FC<UsersServiceProps> = ({
  fetchAndSetUsers,
  searchUsers,
  isLoading,
  isBatchImporting,
  users,
  groups,
  groupMaps,
  paging,
  searchQuery,
  setPage,
  movePage,
  setSearchQuery,
  onDownloadCsv,
  importByCsv,
  updateByCsv,
  downloadTemplateCsv,
}) => {
  const [selectedUsers, setSelectedUsers] = React.useState<UserEntity[]>([]);
  const [authMethodChangableToSAML, setAuthMethodChangableToSAML] =
    React.useState<boolean>(false);
  const [authMethodChangableToPassword, setAuthMethodChangableToPassword] =
    React.useState<boolean>(false);

  const [hasSAMLSetting, setHasSAMLSetting] = React.useState<boolean>(false);

  const history = useHistory();

  const authState = useAuthContext();
  const orgId = authState.data?.orgId;

  const csvImportModal = useModal();
  const csvUpdateModal = useModal();
  const csvDownloadModal = useModal();
  const batchAuthChangeMethodModalSAML = useModal();
  const batchAuthChangeMethodModalPassword = useModal();
  const dropdownModal = useModal();

  const batchAuthChangeMethodModalSAMLClose = () => {
    batchAuthChangeMethodModalSAML.close();
    setSelectedUsers([]);
    fetchAndSetUsers();
  };

  const batchAuthChangeMethodModalPasswordClose = () => {
    batchAuthChangeMethodModalPassword.close();
    setSelectedUsers([]);
    fetchAndSetUsers();
  };

  useEffect(() => {
    if (!orgId) {
      return;
    }

    ChangeAuthMethodService.HasSAMLSetting(orgId).then((res) => {
      setHasSAMLSetting(res);
    });
  }, [orgId]);

  const { page, q, groupId, disabled, authMethod } = parseQueryOfLocation(
    useLocation(),
  ) as {
    page: string | undefined;
    q: string | undefined;
    groupId: string | undefined;
    disabled: string | undefined;
    authMethod: string | undefined;
  };

  const onSubmitSearch = () => {
    // NOTE: 検索条件変更時に選択したユーザーをリセットしておかないと、検索前に選択したユーザーが選択されたままになる
    setSelectedUsers([]);
    searchUsers();
  };

  useEffect(() => {
    setPage(parseInt(page!, 10) || 1);
    setSearchQuery({ q, groupId, disabled, authMethod });
    fetchAndSetUsers();
  }, [
    page,
    q,
    groupId,
    disabled,
    authMethod,
    fetchAndSetUsers,
    setPage,
    setSearchQuery,
  ]);

  useEffect(() => {
    // NOTE(morita): 選択されたユーザーが全て同じ認証方式の場合のみ、認証方式を変更できるようにしている
    // 選択をしてドロップダウンメニューを開くという操作をしなければ、認証方式を変更できる条件に気付けないので、あまり良い UI とはいえないかもしれない
    // 認証方式変更のユースケース的に、選択したユーザーの認証方式が混在していることはそこまで多くないと思うので、このような形にしているが、
    // 変更のモーダル内でエラーを表示するなどの方法も検討できる
    if (selectedUsers.length === 0) {
      setAuthMethodChangableToSAML(false);
      setAuthMethodChangableToPassword(false);
      return;
    }

    setAuthMethodChangableToPassword(
      selectedUsers.every((user) => user.authMethod === "saml"),
    );
    setAuthMethodChangableToSAML(
      selectedUsers.every((user) => user.authMethod === "password"),
    );
  }, [selectedUsers]);

  if (!authState.hasPermissions("users.read")) {
    return <Redirect to={SettingR.MyAccount.link({})} />;
  }

  return (
    <SettingsTemplate breadcrumbs={[{ to: "#", title: "ユーザー一覧" }]}>
      <h1 className="title">
        <i className="fa fa-user" /> ユーザー一覧
      </h1>
      <div>
        <nav className="level">
          <div className="level-left"></div>
          <div className="level-right">
            <div className="level-item">
              <RBACButton
                requirePermissions={"users.read"}
                onClick={csvDownloadModal.open}
                buttonTypes="secondary"
                buttonText="CSV出力"
              />
            </div>
            <div className="level-item">
              <OutsideClickHandler onOutsideClick={dropdownModal.close}>
                <Dropdown
                  title="一括操作"
                  isActive={dropdownModal.isActive}
                  onClick={dropdownModal.open}
                  menus={[
                    {
                      title: "一括登録",
                      onClick: csvImportModal.open,
                      disable: !authState.hasPermissions("users.create"),
                    },
                    {
                      title: "一括更新",
                      onClick: csvUpdateModal.open,
                      disable: !authState.hasPermissions("users.update"),
                    },
                    hasSAMLSetting && {
                      title: "認証方式をSAMLに変更する",
                      onClick: batchAuthChangeMethodModalSAML.open,
                      disable: !authMethodChangableToSAML,
                    },
                    hasSAMLSetting && {
                      title: "認証方式をパスワードに変更する",
                      onClick: batchAuthChangeMethodModalPassword.open,
                      disable: !authMethodChangableToPassword,
                    },
                  ]}
                />
              </OutsideClickHandler>
            </div>
            <div className="level-item">
              <RBACButton
                requirePermissions={"users.create"}
                onClick={() => {
                  history.push(SettingR.UsersNew.link({}));
                }}
                buttonTypes="primary"
                buttonText="新規登録"
              />
            </div>
          </div>
        </nav>
        <nav className="level">
          <div className="level-item">
            <UsersSearchContainer
              groups={groups}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              searchUsers={onSubmitSearch}
            />
          </div>
        </nav>
        <Loader isLoading={isLoading}>
          <TenantUserList
            users={users}
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
            groupMaps={groupMaps}
            selectable={hasSAMLSetting}
          />
          {users.length === 0 &&
            matchType(!searchQuery.q ? "no_registered" : "no_results", {
              no_registered: (
                <div className="py-2">
                  <InformationBanner
                    message="ユーザーがまだ登録されていません。"
                    description="「新規登録」より、ユーザーを登録してください。"
                  />
                </div>
              ),
              no_results: (
                <div className="py-2">
                  <InformationBanner
                    message="条件に該当するユーザーが見つかりませんでした。"
                    description="検索条件を確認してください。"
                  />
                </div>
              ),
            })}
        </Loader>
        <div style={{ marginTop: "16px" }}>
          <Pagination
            onPageClick={movePage}
            currentPage={paging.current}
            lastPage={paging.total}
          />
        </div>
      </div>
      {orgId && csvImportModal.isActive && (
        <BatchImportCreateUserModal
          orgId={orgId}
          isBatchImporting={isBatchImporting}
          isActive={csvImportModal.isActive}
          onClose={csvImportModal.close}
          onImport={importByCsv}
          onDownloadTemplateCsv={downloadTemplateCsv}
          fetchUsersByOrganizationId={fetchAndSetUsers}
        />
      )}
      {orgId && csvUpdateModal.isActive && (
        <BatchImportUpdateUserModal
          orgId={orgId}
          isBatchImporting={isBatchImporting}
          isActive={csvUpdateModal.isActive}
          onClose={csvUpdateModal.close}
          onImport={updateByCsv}
        />
      )}
      {orgId && csvDownloadModal.isActive && (
        <UserCsvDownloadModal
          isActive={csvDownloadModal.isActive}
          onClose={csvDownloadModal.close}
          onDownload={(isOutputUserId) => onDownloadCsv(orgId, isOutputUserId)}
        />
      )}
      {orgId && batchAuthChangeMethodModalSAML.isActive && (
        <BatchChangeAuthMethodToSAMLModal
          orgId={orgId}
          onCloseModal={batchAuthChangeMethodModalSAMLClose}
          isActive={batchAuthChangeMethodModalSAML.isActive}
          users={selectedUsers}
        />
      )}
      {orgId && batchAuthChangeMethodModalPassword.isActive && (
        <BatchChangeAuthMethodToPasswordModal
          orgId={orgId}
          onCloseModal={batchAuthChangeMethodModalPasswordClose}
          isActive={batchAuthChangeMethodModalPassword.isActive}
          users={selectedUsers}
        />
      )}
    </SettingsTemplate>
  );
};

export const UsersContainer = connectPageService(
  UsersPage,
  () => new UsersService(),
);
